// --------------------------------------------------------------
// Created On: 2024-05-08
// Author: KC Willard
//
// Last Modified: 2025-11-18
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import { API } from "../../constants/miscellaneous";
import useApi from "../../hooks/useApi";
import isoToTimePassed from "../../utilities/time/isoToTimePassed";
import isoUtcAddDays from "../../utilities/time/isoUtcAddDays";
import isPacificDaylightTime from "../../utilities/time/isPacificDaylightTime";
import DotLoader from "../DotLoader/DotLoader";
import styles from "./LeadTime.module.scss";
import {
  getChristmas,
  getEaster,
  getThanksgiving,
  getLaborDay,
  getMemorialDay,
  getIndependenceDay,
  getNewYearsDay,
} from "date-fns-holiday-us";
import { addDays, format } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";

// Component that makes an api call to retrieve the lead time for the given part number and quantity.
export default function LeadTime(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [leadTime, setLeadTime] = useState<string>("");

  const partHasPaintColor: boolean = props.paintColorId !== null;
  const partHasModifiedImpellerAngle: boolean =
    props.isImpeller &&
    props.impellerAngle !== null &&
    !isNaN(parseFloat(props.impellerAngle)) &&
    parseFloat(props.impellerAngle) > 0;
  const partHasModifiedImpellerTrim: boolean =
    props.isImpeller &&
    props.impellerTrim !== null &&
    !isNaN(parseInt(props.impellerTrim, 10)) &&
    parseInt(props.impellerTrim, 10) >= 1;

  // Get the lead time for when part(s) will be shipped.
  useApi(
    () => {
      if (props.partNumber === "" || props.quantity === "") {
        setLoading(true);
        return false;
      } else {
        setLoading(true);
        return true;
      }
    },
    {
      url: `${API}/leadtime`,
      method: "POST",
      body: { partNum: props.partNumber, quantity: props.quantity },
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (props.quantity !== "") {
        if (response.ok && responseBody) {
          // The Epicor lead time date uses the following format '2025-02-24T00:00:00.000'.
          // The date is somewhat misleading as the timezone is PST/PDT and the timestamp is always 00:00:00.000.
          // This means that we have to adjust the timestamp if we want to keep track of a UTC date.
          // Since PST/PDT is -8/-7 hours from UTC and the timestamp always represents a start of a date (00:00:00.000),
          // we first determine if we are currently in PST or PDT and add the appropriate amount of hours.
          let epicorDateAvailable = responseBody.dateAvailable;
          if (isPacificDaylightTime(epicorDateAvailable)) {
            epicorDateAvailable = epicorDateAvailable.replace("T00", "T07");
          } else {
            epicorDateAvailable = epicorDateAvailable.replace("T00", "T08");
          }
          calculateLeadTime(
            epicorDateAvailable,
            partHasPaintColor,
            partHasModifiedImpellerAngle,
            partHasModifiedImpellerTrim
          );
          setFailedToLoad(false);
        } else {
          setFailedToLoad(true);
        }
      }
      setLoading(false);
    },
    [props.partNumber, props.quantity, props.paintColorId, props.impellerAngle, props.impellerTrim]
  );

  // Calculate a part's lead time based on selected modifications.
  async function calculateLeadTime(
    dateAvailable: string,
    partHasPaintColor: boolean,
    partHasModifiedImpellerAngle: boolean,
    partHasModifiedImpellerTrim: boolean
  ) {
    let dateUtc: string = dateAvailable;
    // Always add one day of time to the date available per the sales team's instructions.
    dateUtc = isoUtcAddDays(dateUtc, 1);
    // If a paint color is selected, add 1 day to lead time.
    if (partHasPaintColor) {
      dateUtc = isoUtcAddDays(dateUtc, 1);
    }
    // If there are impeller modifications, add 2 days to lead time.
    if (partHasModifiedImpellerAngle || partHasModifiedImpellerTrim) {
      dateUtc = isoUtcAddDays(dateUtc, 2);
    }
    dateUtc = addBusinessDays(dateUtc);
    setLeadTime(isoToTimePassed(dateUtc));
    props.onLeadTimeCalculated(dateUtc);
  }

  // Function to calculate if a given date falls on a holiday or a weekend and adds days until it no longer does.
  function addBusinessDays(isoUtc: string) {
    const date = new Date(isoUtc);
    const currentYear = date.getFullYear();
    const nextYear = date.getFullYear() + 1;
    const thanksgiving = getDateAsPacific(getThanksgiving(currentYear));
    const dayAfterThanksgiving = addDays(thanksgiving, 1);
    const nextThanksgiving = getDateAsPacific(getThanksgiving(nextYear));
    const dayAfterThanksgivingNextYear = addDays(nextThanksgiving, 1);
    const christmas = getDateAsPacific(getChristmas(currentYear));
    const dayAfterChristmas = addDays(christmas, 1);
    const nextChristmas = getDateAsPacific(getChristmas(nextYear));
    const dayAfterChristmasNextYear = addDays(nextChristmas, 1);
    const holidays = [
      christmas.toISOString(),
      dayAfterChristmas.toISOString(),
      getDateAsPacific(getEaster(currentYear)).toISOString(),
      thanksgiving.toISOString(),
      dayAfterThanksgiving.toISOString(),
      getDateAsPacific(getLaborDay(currentYear)).toISOString(),
      getDateAsPacific(getMemorialDay(currentYear)).toISOString(),
      getDateAsPacific(getIndependenceDay(currentYear)).toISOString(),
      getDateAsPacific(getNewYearsDay(currentYear)).toISOString(),
      nextChristmas.toISOString(),
      dayAfterChristmasNextYear.toISOString(),
      getDateAsPacific(getEaster(nextYear)).toISOString(),
      nextThanksgiving.toISOString(),
      dayAfterThanksgivingNextYear.toISOString(),
      getDateAsPacific(getLaborDay(nextYear)).toISOString(),
      getDateAsPacific(getMemorialDay(nextYear)).toISOString(),
      getDateAsPacific(getIndependenceDay(nextYear)).toISOString(),
      getDateAsPacific(getNewYearsDay(nextYear)).toISOString(),
    ];
    // Gets if a specific date is a holiday using UTC time.
    const isHoliday = (date: Date) => holidays.includes(date.toISOString());

    // Gets the day of the week based on UTC time. 0 is Sunday and 6 is Saturday.
    const isWeekend = (date: Date) => {
      return date.getUTCDay() === 0 || date.getUTCDay() === 6;
    };
    // Add days until the date no longer falls on a weekend or holiday.
    while (isWeekend(date) || isHoliday(date)) {
      date.setDate(date.getDate() + 1);
    }
    return date.toISOString();
  }

  // Modifies a date so that it returns as if local time was pacific.
  // We don't simply drop the times from all dates since that would mean that when we calculate a next day
  // (for thanksgiving or christmas) we could be off by up to 8 hours.
  function getDateAsPacific(localDate: Date): Date {
    const pacificTimeZone = "America/Los_Angeles";
    const dateStr = format(localDate, "yyyy-MM-dd");
    const pacificUtc = zonedTimeToUtc(`${dateStr}T00:00:00`, pacificTimeZone);
    return pacificUtc;
  }

  return failedToLoad ? (
    <div>Unable to calculate lead time.</div>
  ) : (
    <div className={`${styles.row} row`}>
      <div data-test="lead-time" className="col-12">
        {loading ? <DotLoader loading={loading} className={props.className} /> : <span>{leadTime}</span>}
      </div>
    </div>
  );
}

interface Props {
  partNumber: string;
  quantity: string;
  paintColorId: number | null;
  isImpeller: boolean;
  impellerTrim: string | null;
  impellerAngle: string | null;
  className: string;
  onLeadTimeCalculated: (dateAvailable: string) => void;
}

interface ResponseBody {
  dateAvailable: string;
}
